/**
 * SVG layout post-processor.
 *
 * The AI emits inline SVG flowcharts with hand-typed x/y/width/height. It
 * has no way to measure how wide a label will render, so blocks come out
 * inconsistent: long labels overflow, short labels leave dead space, and
 * vertical gaps drift between rows. This pass:
 *
 *   1. Renders the SVG hidden in the DOM to measure every <text> via
 *      getBBox.
 *   2. Pairs each <text> with the <rect> it sits inside (either by the
 *      AI-emitted <g data-block="id"> convention from the system prompt,
 *      or by coordinate containment as a fallback for legacy output).
 *   3. Resizes each rect to fit its text plus uniform padding.
 *   4. Snaps rects onto a uniform vertical pitch per row so gaps are
 *      equal regardless of the AI's original y values.
 *   5. Reroutes every <line>/<path d="M…L…"> arrow between the nearest
 *      edges of its from/to blocks.
 *
 * The entire pass is wrapped in try/catch — if anything goes sideways
 * (malformed input, unexpected structure) we return the original SVG
 * unchanged so streaming UX never regresses.
 *
 * Returns the rewritten SVG outerHTML, or the original string if the
 * input isn't a recognisable blocks-and-arrows flowchart.
 */

const PADDING_X = 16;
const PADDING_Y = 12;
const MIN_WIDTH = 120;
const MIN_HEIGHT = 56;
const ROW_GAP = 60;       // vertical gap between rows
const COL_GAP = 40;       // horizontal gap between columns in a row
const ROW_BUCKET = 30;    // y values within this many px count as same row

const measureHidden = (svgEl) => {
  const host = document.createElement('div');
  host.style.cssText = 'position:absolute;left:-99999px;top:0;width:0;height:0;overflow:hidden;visibility:hidden;';
  const clone = svgEl.cloneNode(true);
  host.appendChild(clone);
  document.body.appendChild(host);
  return { host, clone };
};

const cleanup = (host) => { try { document.body.removeChild(host); } catch {} };

const textBBox = (textEl) => {
  try {
    const b = textEl.getBBox();
    return { w: b.width, h: b.height };
  } catch {
    return null;
  }
};

const rectCenter = (r) => {
  const x = parseFloat(r.getAttribute('x') || '0');
  const y = parseFloat(r.getAttribute('y') || '0');
  const w = parseFloat(r.getAttribute('width') || '0');
  const h = parseFloat(r.getAttribute('height') || '0');
  return { cx: x + w / 2, cy: y + h / 2, x, y, w, h };
};

const pairBlocks = (svgEl) => {
  // Preferred: AI used <g data-block="id"> wrapping rect + text.
  const tagged = Array.from(svgEl.querySelectorAll('g[data-block]'));
  if (tagged.length > 0) {
    return tagged.map((g, idx) => ({
      id: g.getAttribute('data-block') || `b${idx}`,
      group: g,
      rect: g.querySelector('rect'),
      texts: Array.from(g.querySelectorAll('text')),
    })).filter(b => b.rect);
  }
  // Fallback: coordinate-based pairing.
  const rects = Array.from(svgEl.querySelectorAll('rect'))
    .filter(r => parseFloat(r.getAttribute('width') || '0') >= 50 && parseFloat(r.getAttribute('height') || '0') >= 20);
  const texts = Array.from(svgEl.querySelectorAll('text'));
  return rects.map((r, idx) => {
    const { x, y, w, h } = rectCenter(r);
    const containedTexts = texts.filter(t => {
      const tx = parseFloat(t.getAttribute('x') || '0');
      const ty = parseFloat(t.getAttribute('y') || '0');
      return tx >= x - 2 && tx <= x + w + 2 && ty >= y - 4 && ty <= y + h + 8;
    });
    return { id: `r${idx}`, group: r.parentNode, rect: r, texts: containedTexts };
  });
};

const computeNewSize = (block) => {
  let maxW = 0, totalH = 0;
  block.texts.forEach(t => {
    const bb = textBBox(t);
    if (!bb) return;
    maxW = Math.max(maxW, bb.w);
    totalH += bb.h * 1.2;
  });
  if (block.texts.length === 0) {
    const r = rectCenter(block.rect);
    return { w: Math.max(MIN_WIDTH, r.w), h: Math.max(MIN_HEIGHT, r.h) };
  }
  return {
    w: Math.max(MIN_WIDTH, Math.round(maxW + PADDING_X * 2)),
    h: Math.max(MIN_HEIGHT, Math.round(totalH + PADDING_Y * 2)),
  };
};

const groupByRow = (blocks) => {
  // Sort by original cy, then bucket cy values within ROW_BUCKET into rows.
  const enriched = blocks.map(b => ({ ...b, ...rectCenter(b.rect) }))
    .sort((a, b) => a.cy - b.cy);
  const rows = [];
  let current = null;
  enriched.forEach(b => {
    if (!current || Math.abs(b.cy - current.cy) > ROW_BUCKET) {
      current = { cy: b.cy, items: [b] };
      rows.push(current);
    } else {
      current.items.push(b);
    }
  });
  rows.forEach(row => row.items.sort((a, b) => a.cx - b.cx));
  return rows;
};

const applyLayout = (rows, sizes) => {
  // ONE global center x for the whole diagram, so single-block rows
  // line up vertically with the centre column of multi-block rows and
  // arrows between them stay perfectly vertical. Uniform column pitch
  // = widest block + COL_GAP, so every column gets the same horizontal
  // slot regardless of label length variation. Within each row blocks
  // are placed symmetrically around globalCx at integer multiples of
  // cellPitch — this preserves the AI's left/centre/right intent
  // without inheriting its uneven coordinates.
  const placement = new Map();
  const allBlocks = rows.flatMap(r => r.items);
  if (allBlocks.length === 0) return placement;

  const globalCx = allBlocks.reduce((sum, b) => sum + b.cx, 0) / allBlocks.length;
  const maxW = Math.max(...allBlocks.map(b => sizes.get(b.id).w));
  const cellPitch = maxW + COL_GAP;

  let yCursor = rows[0].items[0].y;
  rows.forEach(row => {
    const rowH = Math.max(...row.items.map(b => sizes.get(b.id).h));
    const n = row.items.length;
    row.items.forEach((b, idx) => {
      const sz = sizes.get(b.id);
      const colOffset = (idx - (n - 1) / 2) * cellPitch;
      const cx = globalCx + colOffset;
      const cy = yCursor + rowH / 2;
      placement.set(b.id, {
        x: cx - sz.w / 2,
        y: yCursor + (rowH - sz.h) / 2,
        w: sz.w, h: sz.h,
        cx, cy,
        block: b,
      });
    });
    yCursor += rowH + ROW_GAP;
  });
  return placement;
};

const applyToRect = (rect, p) => {
  rect.setAttribute('x', String(Math.round(p.x)));
  rect.setAttribute('y', String(Math.round(p.y)));
  rect.setAttribute('width', String(Math.round(p.w)));
  rect.setAttribute('height', String(Math.round(p.h)));
};

const applyToTexts = (texts, p) => {
  // Center the text stack vertically inside the new rect. Preserve
  // text-anchor="middle" patterns by snapping x to rect center.
  const stack = texts.length;
  if (stack === 0) return;
  const lineH = Math.max(18, Math.round(p.h / (stack + 0.5)));
  const startY = p.y + (p.h - lineH * (stack - 1)) / 2;
  texts.forEach((t, i) => {
    t.setAttribute('x', String(Math.round(p.cx)));
    t.setAttribute('y', String(Math.round(startY + i * lineH)));
    t.setAttribute('text-anchor', 'middle');
    // Clear any tspan dy offsets so the parent y wins.
    Array.from(t.querySelectorAll('tspan')).forEach(ts => {
      ts.setAttribute('x', String(Math.round(p.cx)));
      ts.removeAttribute('dy');
    });
  });
};

const nearestEdge = (placement, fromId, toId) => {
  const a = placement.get(fromId);
  const b = placement.get(toId);
  if (!a || !b) return null;
  // Pick the edge of `a` that points toward `b`, and the opposite edge of `b`.
  const dx = b.cx - a.cx;
  const dy = b.cy - a.cy;
  let from, to;
  if (Math.abs(dy) >= Math.abs(dx)) {
    from = { x: a.cx, y: dy >= 0 ? a.y + a.h : a.y };
    to   = { x: b.cx, y: dy >= 0 ? b.y : b.y + b.h };
  } else {
    from = { x: dx >= 0 ? a.x + a.w : a.x, y: a.cy };
    to   = { x: dx >= 0 ? b.x : b.x + b.w, y: b.cy };
  }
  return { from, to };
};

const findEnclosingBlock = (placement, x, y, tolerance = 8) => {
  for (const [id, p] of placement) {
    if (x >= p.x - tolerance && x <= p.x + p.w + tolerance &&
        y >= p.y - tolerance && y <= p.y + p.h + tolerance) {
      return id;
    }
  }
  return null;
};

const rerouteArrows = (svgEl, placement, originalCoords) => {
  // Tagged arrows first: <path data-arrow data-from data-to>.
  const tagged = Array.from(svgEl.querySelectorAll('[data-arrow]'));
  tagged.forEach(el => {
    const fromId = el.getAttribute('data-from');
    const toId = el.getAttribute('data-to');
    const edge = nearestEdge(placement, fromId, toId);
    if (!edge) return;
    el.setAttribute('d',
      `M${Math.round(edge.from.x)} ${Math.round(edge.from.y)} L${Math.round(edge.to.x)} ${Math.round(edge.to.y)}`);
  });
  if (tagged.length > 0) return;

  // Fallback: rewrite every <line> and simple <path d="M…L…"> by mapping
  // its original endpoints back to blocks via the pre-layout coordinates,
  // then redrawing between the post-layout block edges.
  const arrows = [
    ...Array.from(svgEl.querySelectorAll('line')),
    ...Array.from(svgEl.querySelectorAll('path')),
  ];
  arrows.forEach(el => {
    let x1, y1, x2, y2;
    if (el.tagName.toLowerCase() === 'line') {
      x1 = parseFloat(el.getAttribute('x1') || 'NaN');
      y1 = parseFloat(el.getAttribute('y1') || 'NaN');
      x2 = parseFloat(el.getAttribute('x2') || 'NaN');
      y2 = parseFloat(el.getAttribute('y2') || 'NaN');
    } else {
      const d = el.getAttribute('d') || '';
      const m = d.match(/M\s*(-?[\d.]+)[\s,]+(-?[\d.]+)\s*L\s*(-?[\d.]+)[\s,]+(-?[\d.]+)/i);
      if (!m) return;
      x1 = parseFloat(m[1]); y1 = parseFloat(m[2]);
      x2 = parseFloat(m[3]); y2 = parseFloat(m[4]);
    }
    if ([x1, y1, x2, y2].some(v => Number.isNaN(v))) return;
    const fromId = findEnclosingBlock(originalCoords, x1, y1);
    const toId   = findEnclosingBlock(originalCoords, x2, y2);
    if (!fromId || !toId || fromId === toId) return;
    const edge = nearestEdge(placement, fromId, toId);
    if (!edge) return;
    if (el.tagName.toLowerCase() === 'line') {
      el.setAttribute('x1', String(Math.round(edge.from.x)));
      el.setAttribute('y1', String(Math.round(edge.from.y)));
      el.setAttribute('x2', String(Math.round(edge.to.x)));
      el.setAttribute('y2', String(Math.round(edge.to.y)));
    } else {
      el.setAttribute('d',
        `M${Math.round(edge.from.x)} ${Math.round(edge.from.y)} L${Math.round(edge.to.x)} ${Math.round(edge.to.y)}`);
    }
  });
};

const fitViewBox = (svgEl, placement) => {
  if (placement.size === 0) return;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  placement.forEach(p => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x + p.w);
    maxY = Math.max(maxY, p.y + p.h);
  });
  const margin = 24;
  svgEl.setAttribute('viewBox',
    `${Math.round(minX - margin)} ${Math.round(minY - margin)} ${Math.round((maxX - minX) + margin * 2)} ${Math.round((maxY - minY) + margin * 2)}`);
};

const looksLikeFlowchart = (svgEl) => {
  const rects = svgEl.querySelectorAll('rect').length;
  const lines = svgEl.querySelectorAll('line').length;
  const paths = svgEl.querySelectorAll('path').length;
  return rects >= 2 && (lines + paths) >= 1;
};

export const layoutSvg = (svgString) => {
  if (typeof window === 'undefined' || !svgString) return svgString;
  let host = null;
  try {
    const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return svgString;
    const svgEl = doc.documentElement;
    if (!svgEl || svgEl.tagName.toLowerCase() !== 'svg') return svgString;
    if (!looksLikeFlowchart(svgEl)) return svgString;

    const measureCtx = measureHidden(svgEl);
    host = measureCtx.host;
    const liveSvg = measureCtx.clone;
    const blocks = pairBlocks(liveSvg);
    if (blocks.length < 2) return svgString;

    const sizes = new Map();
    blocks.forEach(b => sizes.set(b.id, computeNewSize(b)));
    const rows = groupByRow(blocks);
    const placement = applyLayout(rows, sizes);

    // Capture original coordinates BEFORE we rewrite anything, so arrow
    // endpoints can still be mapped back to their source block.
    const originalCoords = new Map();
    blocks.forEach(b => { originalCoords.set(b.id, rectCenter(b.rect)); });

    // Apply to a working copy (the parsed doc, not the hidden clone).
    const liveBlocks = pairBlocks(svgEl);
    liveBlocks.forEach(b => {
      const p = placement.get(b.id);
      if (!p) return;
      applyToRect(b.rect, p);
      applyToTexts(b.texts, p);
    });

    rerouteArrows(svgEl, placement, originalCoords);
    fitViewBox(svgEl, placement);

    return svgEl.outerHTML;
  } catch {
    return svgString;
  } finally {
    if (host) cleanup(host);
  }
};
